import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserLayout } from '../components/User/UserLayout';
import { useAuthStore } from '../store/authStore';
import { User, Mail, Lock, Save, Shield, Calendar, Globe } from 'lucide-react';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.hitzlink.com/api';

type NotificationLocale = 'en' | 'th';

export const ProfileSettings: React.FC = () => {
  const { t, i18n } = useTranslation('billing');
  const { t: tCommon } = useTranslation('common');
  const { user, updateUser } = useAuthStore();

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [notificationLocale, setNotificationLocale] =
    useState<NotificationLocale>('en');
  const [savingLocale, setSavingLocale] = useState(false);

  // Load the persisted locale on mount so the dropdown reflects what
  // the server actually has — the auth store doesn't carry it today.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    fetch(`${API_URL}/auth/me/locale`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (cancelled || !j) return;
        if (j.locale === 'th' || j.locale === 'en') {
          setNotificationLocale(j.locale);
        }
      })
      .catch(() => {
        // Pre-migration / network blip — fall back to the default 'en'
        // and let the user re-select if they want.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSaveProfile = async () => {
    if (!formData.name || !formData.email) {
      toast.error(t('profile.toast.nameEmailRequired'));
      return;
    }

    try {
      await updateUser({
        name: formData.name,
        email: formData.email,
      });
      toast.success(t('profile.toast.profileUpdated'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('profile.toast.updateFailed'));
    }
  };

  const handleSaveNotificationLocale = async (next: NotificationLocale) => {
    setNotificationLocale(next);
    setSavingLocale(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/auth/me/locale`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ locale: next }),
      });
      if (!res.ok) throw new Error(String(res.status));
      toast.success(tCommon('settings.notificationLanguage.saved'));
    } catch (err) {
      console.error('[profile] saveNotificationLocale failed', err);
      toast.error(tCommon('settings.notificationLanguage.saveFailed'));
    } finally {
      setSavingLocale(false);
    }
  };

  const handleChangePassword = () => {
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      toast.error(t('profile.toast.passwordsRequired'));
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error(t('profile.toast.passwordMismatch'));
      return;
    }

    if (formData.newPassword.length < 6) {
      toast.error(t('profile.toast.passwordTooShort'));
      return;
    }

    toast.success(t('profile.toast.passwordChanged'));
    setFormData({
      ...formData,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  return (
    <UserLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('profile.title')}</h1>
          <p className="text-gray-600 mt-1">{t('profile.subtitle')}</p>
        </div>

        <div className="max-w-4xl">
          <div className="grid gap-6">
            {/* Account Info Card */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-gray-900 text-2xl font-bold">
                  {user?.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{user?.name}</h2>
                  <p className="text-sm text-gray-600">{user?.email}</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-xs text-gray-600">{t('profile.subscription')}</p>
                    <p className="font-semibold text-gray-900">
                      {user?.subscription === 'pro'
                        ? t('profile.proPlan')
                        : user?.subscription === 'plus'
                        ? t('profile.plusPlan')
                        : t('profile.freePlan')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="text-xs text-gray-600">{t('profile.memberSince')}</p>
                    <p className="font-semibold text-gray-900">
                      {new Date(user?.createdAt || Date.now()).toLocaleDateString(
                        i18n.language === 'th' ? 'th-TH' : undefined,
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Information */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                {t('profile.section.info')}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.fields.fullName')}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('profile.fields.fullNamePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.fields.email')}</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-600" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('profile.fields.emailPlaceholder')}
                    />
                  </div>
                </div>

                <button
                  onClick={handleSaveProfile}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-gray-900 rounded-lg font-medium transition-colors"
                >
                  <Save className="w-5 h-5" />
                  {t('profile.actions.saveProfile')}
                </button>
              </div>
            </div>

            {/* Notification Language (Phase 10) */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                <Globe className="w-5 h-5 text-emerald-600" />
                {tCommon('settings.notificationLanguage.label')}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {tCommon('settings.notificationLanguage.hint')}
              </p>
              <select
                value={notificationLocale}
                onChange={(e) =>
                  handleSaveNotificationLocale(e.target.value as NotificationLocale)
                }
                disabled={savingLocale}
                className="w-full md:w-72 px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-60"
              >
                <option value="en">
                  {tCommon('settings.notificationLanguage.options.en')}
                </option>
                <option value="th">
                  {tCommon('settings.notificationLanguage.options.th')}
                </option>
              </select>
            </div>

            {/* Change Password */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Lock className="w-5 h-5 text-purple-600" />
                {t('profile.section.password')}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.fields.currentPassword')}</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder={t('profile.fields.currentPasswordPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.fields.newPassword')}</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder={t('profile.fields.newPasswordPlaceholder')}
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('profile.fields.newPasswordHint')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.fields.confirmPassword')}</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder={t('profile.fields.confirmPasswordPlaceholder')}
                  />
                </div>

                <button
                  onClick={handleChangePassword}
                  className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-gray-900 rounded-lg font-medium transition-colors"
                >
                  <Lock className="w-5 h-5" />
                  {t('profile.actions.updatePassword')}
                </button>
              </div>
            </div>

            {/* Meta Conversions API has moved to its own top-level page
                (sidebar → Meta CAPI). Account-level default token + test
                event code are configured there. */}

            {/* Danger Zone */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-red-900 mb-2">{t('profile.section.danger')}</h3>
              <p className="text-sm text-red-700 mb-4">
                {t('profile.danger.warning')}
              </p>
              <button
                onClick={() => toast.error(t('profile.danger.demoNotice'))}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-gray-900 rounded-lg font-medium transition-colors"
              >
                {t('profile.actions.deleteAccount')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </UserLayout>
  );
};
